import type { ReactElement } from 'react';
import { matchPath, useLocation, Link } from 'react-router-dom';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project-imports
import Dot from 'components/@extended/Dot';
import IconButton from 'components/@extended/IconButton';

// third-party
import { FormattedMessage } from 'react-intl';

import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import { MenuOrientation, NavActionType } from 'config';
import useConfig from 'hooks/useConfig';

// types
import { LinkTarget, NavItemType } from 'types/menu';

interface Props {
  item: NavItemType;
  level: number;
  isParents?: boolean;
  setSelectedID?: Function;
}

/** Horizontal padding for drawer list items (matches NavCollapse). */
function navDrawerIndentPadding(level: number, drawerOpen: boolean): number {
  if (level === 2) return 3.25;
  if (!drawerOpen) return 1.5;
  if (level <= 3) return (level * 20) / 8;
  return (level * 20 + (level - 3) * 10) / 8;
}

function NavItemVerticalDrawerRow(props: Readonly<{
  item: NavItemType;
  level: number;
  drawerOpen: boolean;
  isSelected: boolean;
  itemTarget: LinkTarget;
  itemIcon: ReactElement | false;
  iconSelectedColor: string;
  onItemClick: () => void;
}>) {
  const { item, level, drawerOpen, isSelected, itemTarget, itemIcon, iconSelectedColor, onItemClick } = props;

  return (
    <Box sx={{ position: 'relative' }}>
      <ListItemButton
        component={Link}
        to={item.url!}
        target={itemTarget}
        disabled={item.disabled}
        selected={isSelected}
        sx={(theme) => ({
          zIndex: 1201,
          pl: navDrawerIndentPadding(level, drawerOpen),
          py: !drawerOpen && level === 1 ? 1.25 : 1,
          ...(drawerOpen && {
            '&:hover': { bgcolor: 'transparent' },
            '&.Mui-selected': { '&:hover': { bgcolor: 'transparent' }, bgcolor: 'transparent' }
          }),
          ...(drawerOpen &&
            level === 1 && {
              mx: 1.25,
              my: 0.5,
              borderRadius: 1,
              '&:hover': { bgcolor: 'secondary.200', ...theme.applyStyles('dark', { bgcolor: 'divider' }) }
            }),
          ...(!drawerOpen && {
            px: 2.75,
            justifyContent: 'center',
            '&:hover': { bgcolor: 'transparent' },
            '&.Mui-selected': { '&:hover': { bgcolor: 'transparent' }, bgcolor: 'transparent' }
          })
        })}
        onClick={() => onItemClick()}
      >
        {itemIcon && (
          <ListItemIcon
            sx={(theme) => ({
              minWidth: 38,
              color: 'secondary.main',
              ...theme.applyStyles('dark', { color: 'secondary.400' }),
              ...(isSelected && { color: iconSelectedColor }),
              ...(!drawerOpen &&
                level === 1 && {
                  borderRadius: 1,
                  width: 46,
                  height: 46,
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': { bgcolor: 'secondary.200', ...theme.applyStyles('dark', { bgcolor: 'divider' }) }
                }),
              ...(!drawerOpen &&
                isSelected && {
                  bgcolor: 'primary.lighter',
                  '&:hover': { bgcolor: 'primary.lighter' },
                  ...theme.applyStyles('dark', { bgcolor: 'divider', '&:hover': { bgcolor: 'divider' } })
                })
            })}
          >
            {itemIcon}
          </ListItemIcon>
        )}

        {!itemIcon && drawerOpen && (
          <ListItemIcon
            sx={{
              minWidth: 30
            }}
          >
            <Dot size={isSelected ? 6 : 5} color={isSelected ? 'primary' : 'secondary'} />
          </ListItemIcon>
        )}

        {(drawerOpen || (!drawerOpen && level !== 1)) && (
          <ListItemText
            primary={
              <Typography
                variant="h6"
                sx={(theme) => ({
                  color: 'secondary.main',
                  ...theme.applyStyles('dark', { color: 'secondary.400' }),
                  ...(isSelected && { color: iconSelectedColor }),
                  fontWeight: isSelected ? 500 : 400
                })}
              >
                <FormattedMessage id={item.title} />
              </Typography>
            }
          />
        )}
        {(drawerOpen || (!drawerOpen && level !== 1)) && item.chip && (
          <Chip
            color={item.chip.color}
            variant={item.chip.variant}
            size={item.chip.size}
            label={<FormattedMessage id={item.chip.label as string} />}
            avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
          />
        )}
      </ListItemButton>

      {(drawerOpen || (!drawerOpen && level !== 1)) &&
        item.actions?.map((action) => {
          const ActionIcon = action?.icon!;
          const callAction = action?.function;

          return (
            <IconButton
              key={`${item.id ?? item.url ?? 'nav'}-${action.type}-${action.label}`}
              {...(action.type === NavActionType.FUNCTION && {
                onClick: (event) => {
                  event.stopPropagation();
                  callAction();
                }
              })}
              {...(action.type === NavActionType.LINK && {
                component: Link,
                to: action.url,
                target: action.target ? '_blank' : '_self'
              })}
              color={isSelected ? 'primary' : 'secondary'}
              variant="outlined"
              sx={(theme) => ({
                position: 'absolute',
                top: 12,
                right: 10,
                zIndex: 1202,
                width: 20,
                height: 20,
                p: 0.25,
                borderColor: isSelected ? 'primary.light' : 'secondary.light',
                '&:hover': { borderColor: isSelected ? 'primary.main' : 'secondary.main' },
                ...theme.applyStyles('dark', { color: isSelected ? 'primary.main' : 'secondary.400' })
              })}
            >
              <ActionIcon size={12} style={{ marginLeft: 1 }} />
            </IconButton>
          );
        })}
    </Box>
  );
}

function NavItemHorizontalBar(props: Readonly<{
  item: NavItemType;
  level: number;
  isParents: boolean;
  drawerOpen: boolean;
  isSelected: boolean;
  itemTarget: LinkTarget;
  itemIcon: ReactElement | false;
  iconSelectedColor: string;
  onItemClick: () => void;
}>) {
  const { item, level, isParents, drawerOpen, isSelected, itemTarget, itemIcon, iconSelectedColor, onItemClick } = props;

  return (
    <ListItemButton
      component={Link}
      to={item.url!}
      target={itemTarget}
      disabled={item.disabled}
      selected={isSelected}
      sx={(theme) => ({
        zIndex: 1201,
        borderRadius: !isParents && level >= 1 ? 0 : 1,
        height: 46,
        ...(isParents && { color: 'secondary.main', ...theme.applyStyles('dark', { color: 'secondary.400' }), p: 1, mr: 1 }),
        ...(!isParents && {
          '&.Mui-selected': {
            bgcolor: 'transparent',
            color: iconSelectedColor,
            '&:hover': {
              color: iconSelectedColor,
              bgcolor: 'transparent'
            }
          }
        })
      })}
      onClick={() => onItemClick()}
    >
      {itemIcon && (
        <ListItemIcon
          sx={{
            minWidth: 36,
            ...(!drawerOpen && {
              borderRadius: 1,
              width: 36,
              height: 36,
              alignItems: 'center',
              justifyContent: 'flex-start',
              '&:hover': { bgcolor: 'transparent' }
            }),
            ...(!drawerOpen && isSelected && { bgcolor: 'transparent', '&:hover': { bgcolor: 'transparent' } })
          }}
        >
          {itemIcon}
        </ListItemIcon>
      )}

      <ListItemText
        primary={
          <Typography
            variant="h6"
            sx={(theme) => ({
              color: 'secondary.main',
              ...theme.applyStyles('dark', { color: 'secondary.400' }),
              ...(isSelected && { color: iconSelectedColor }),
              fontWeight: isSelected ? 500 : 400
            })}
          >
            <FormattedMessage id={item.title} />
          </Typography>
        }
      />
      {item.chip && (
        <Chip
          color={item.chip.color}
          variant={item.chip.variant}
          size={item.chip.size}
          label={item.chip.label}
          avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
          sx={{ ml: 1 }}
        />
      )}
    </ListItemButton>
  );
}

// ==============================|| NAVIGATION - ITEM ||============================== //

export default function NavItem({ item, level, isParents = false, setSelectedID }: Readonly<Props>) {
  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const { menuOrientation } = useConfig();

  let itemTarget: LinkTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  const Icon = item.icon!;
  const itemIcon = item.icon ? (
    <Icon
      variant="Bulk"
      size={drawerOpen ? 20 : 22}
      style={{ ...(menuOrientation === MenuOrientation.HORIZONTAL && isParents && { fontSize: 20, stroke: '1.5' }) }}
    />
  ) : (
    false
  );

  const { pathname } = useLocation();
  const isSelected = !!matchPath({ path: item?.link ? item.link : item.url!, end: false }, pathname);

  const iconSelectedColor = 'primary.main';

  const itemHandler = () => {
    if (downLG) handlerDrawerOpen(false);

    if (isParents && setSelectedID) {
      setSelectedID();
    }
  };

  return (
    <>
      {menuOrientation === MenuOrientation.VERTICAL || downLG ? (
        <NavItemVerticalDrawerRow
          item={item}
          level={level}
          drawerOpen={drawerOpen}
          isSelected={isSelected}
          itemTarget={itemTarget}
          itemIcon={itemIcon}
          iconSelectedColor={iconSelectedColor}
          onItemClick={itemHandler}
        />
      ) : (
        <NavItemHorizontalBar
          item={item}
          level={level}
          isParents={isParents}
          drawerOpen={drawerOpen}
          isSelected={isSelected}
          itemTarget={itemTarget}
          itemIcon={itemIcon}
          iconSelectedColor={iconSelectedColor}
          onItemClick={itemHandler}
        />
      )}
    </>
  );
}
